import React from "react";
import classNames from "classnames";

import { Space, SpaceTools } from "@/models/spaces";
import { Goal } from "@/models/goals";
import { Project } from "@/models/projects";
import { Discussion } from "@/models/discussions";
import * as Tasks from "@/models/tasks";
import * as People from "@/models/people";
import { crmToolLabel, useGetCrmContacts } from "@/models/crmContacts";

import { ProgressBar } from "@/components/charts";
import { statusColor } from "@/components/status/colors";
import { splitByStatus } from "@/models/milestones";

import { usePaths } from "@/routes/paths";
import { Feed, useItemsQuery } from "@/features/Feed";
import { useSpaceDataRefreshSignal, useReloadContactsSignal } from "@/signals";

import {
  Avatar,
  DivLink,
  AssigneesField,
  StatusSelector,
  NodeIcon,
  sortNodesWithFoldersFirst,
  IconTarget,
  IconMessages,
  IconFileText,
  IconListCheck,
  IconActivity,
  IconUsers,
} from "turboui";

//
// "Bento dashboard" presentation of a space's tools.
//
// This is pure re-presentation: it reads the same `space` + `tools` the page
// already loads (Spaces.listSpaceTools) and renders each tool's latest items as
// a tile sized to its weight — Goals & Projects leads, Activity is a tall tile,
// nothing is a second-class citizen at the bottom. No new queries.
//
export function SpaceBento({ space, tools }: { space: Space; tools: SpaceTools }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 auto-rows-min" data-test-id="space-bento">
      <GoalsProjectsTile space={space} goals={tools.goals ?? []} projects={tools.projects ?? []} />
      {/* Keys include the space id so the tiles that own their own query (Activity,
          Contacts) re-fetch when switching spaces (useQuery only fires on mount, and
          the page component is reused across spaces/:id navigations). The keys must be
          DISTINCT per tile — two siblings sharing a key breaks React reconciliation
          and the old tile gets left mounted (stacking activity cards). */}
      <ActivityTile key={`activity-${space.id}`} space={space} />
      {tools.discussionsEnabled && <DiscussionsTile space={space} tools={tools} />}
      {tools.resourceHubEnabled && <DocumentsTile space={space} tools={tools} />}
      {tools.tasksEnabled && <TasksTile space={space} tasks={tools.tasks ?? []} />}
      <CrmTile key={`crm-${space.id}`} space={space} />
    </div>
  );
}

interface TileProps {
  icon: React.ReactNode;
  iconWrap: string;
  title: string;
  count?: number;
  viewAllTo?: string;
  className?: string;
  testId?: string;
  children: React.ReactNode;
}

function Tile({ icon, iconWrap, title, count, viewAllTo, className, testId, children }: TileProps) {
  return (
    <div
      className={classNames("bg-surface-base border border-stroke-base rounded-2xl p-4 overflow-hidden", className)}
      data-test-id={testId}
    >
      <div className="flex items-center gap-2 mb-3">
        <div className={classNames("w-6 h-6 rounded-lg flex items-center justify-center shrink-0", iconWrap)}>
          {icon}
        </div>
        <span className="font-bold text-content-base truncate">{title}</span>
        {typeof count === "number" && <span className="text-xs text-content-subtle">{count}</span>}
        {viewAllTo && (
          <DivLink
            to={viewAllTo}
            className="ml-auto text-xs font-semibold text-link-base hover:text-link-hover shrink-0"
          >
            View all
          </DivLink>
        )}
      </div>
      {children}
    </div>
  );
}

function EmptyHint({ text }: { text: string }) {
  return <div className="text-sm text-content-subtle py-2">{text}</div>;
}

//
// Goals & Projects — wide tile (spans 2 columns)
//
function GoalsProjectsTile({ space, goals, projects }: { space: Space; goals: Goal[]; projects: Project[] }) {
  const paths = usePaths();
  const openGoals = goals.filter((g) => !g.closedAt);
  const openProjects = projects.filter((p) => p.state !== "closed");
  const count = openGoals.length + openProjects.length;
  const goalItems = openGoals.slice(0, 6);
  const projectItems = openProjects.slice(0, 6 - goalItems.length);

  return (
    <Tile
      className="lg:col-span-2"
      iconWrap="bg-emerald-50 dark:bg-emerald-900/30"
      icon={<IconTarget size={14} className="text-emerald-600 dark:text-emerald-400" />}
      title="Goals & Projects"
      count={count}
      viewAllTo={paths.spaceWorkMapPath(space.id!)}
      testId="bento-goals-projects"
    >
      {count === 0 ? (
        <EmptyHint text="No goals or projects yet" />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
          {goalItems.map((goal) => (
            <GoalRow key={goal.id} goal={goal} />
          ))}
          {projectItems.map((project) => (
            <ProjectRow key={project.id} project={project} />
          ))}
        </div>
      )}
    </Tile>
  );
}

function GoalRow({ goal }: { goal: Goal }) {
  const status = goal.isOutdated ? "outdated" : !goal.lastCheckIn ? "pending" : goal.lastCheckIn.status!;
  const color = statusColor(status);
  const percentage = goal.progressPercentage ?? 0;

  return (
    <div className="border border-stroke-base rounded-lg px-3 py-2.5">
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-medium truncate">{goal.name}</span>
        <span className="text-[10px] text-content-subtle shrink-0">Goal</span>
      </div>
      <div className="mt-2">
        <ProgressBar percentage={percentage} width="w-full" height="h-[6px]" color={color} />
      </div>
    </div>
  );
}

function ProjectRow({ project }: { project: Project }) {
  const total = project.milestones?.length ?? 0;
  const done = project.milestones ? splitByStatus(project.milestones).done.length : 0;
  const percentage = total === 0 ? 0 : (done / total) * 100;
  const color =
    project.state === "paused" ? statusColor("paused") : statusColor(project.lastCheckIn?.status ?? "pending");

  return (
    <div className="border border-stroke-base rounded-lg px-3 py-2.5">
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-medium truncate">{project.name}</span>
        <span className="text-[10px] text-content-subtle shrink-0">Project</span>
      </div>
      <div className="mt-2">
        <ProgressBar percentage={percentage} width="w-full" height="h-[6px]" color={color} />
      </div>
    </div>
  );
}

//
// Activity — tall tile (spans 2 rows), pinned beside Goals & Projects
//
function ActivityTile({ space }: { space: Space }) {
  const { data, loading, error, refetch } = useItemsQuery("space", space.id!);
  useSpaceDataRefreshSignal(refetch);

  return (
    <div
      className="bg-surface-base border border-stroke-base rounded-2xl p-4 overflow-hidden lg:row-span-2"
      data-test-id="bento-activity"
    >
      <div className="flex items-center gap-2 mb-3">
        <div className="w-6 h-6 rounded-lg bg-emerald-50 dark:bg-emerald-900/30 flex items-center justify-center shrink-0">
          <IconActivity size={14} className="text-emerald-600 dark:text-emerald-400" />
        </div>
        <span className="font-bold text-content-base">Activity</span>
      </div>

      <div className="max-h-[520px] overflow-y-auto -mr-2 pr-2">
        {loading ? (
          <div className="text-sm text-content-dimmed py-2">Loading…</div>
        ) : error ? (
          <div className="text-sm text-content-error py-2">Couldn't load activity.</div>
        ) : (
          <Feed items={data?.activities || []} testId="space-feed" page="space" />
        )}
      </div>
    </div>
  );
}

//
// Discussions
//
function DiscussionsTile({ space, tools }: { space: Space; tools: SpaceTools }) {
  const paths = usePaths();
  const all = (tools.messagesBoards ?? []).flatMap((b) => b.messages ?? []);
  const items = all.slice(0, 3);

  return (
    <Tile
      iconWrap="bg-blue-50 dark:bg-blue-900/30"
      icon={<IconMessages size={14} className="text-blue-600 dark:text-blue-400" />}
      title="Discussions"
      count={all.length}
      viewAllTo={paths.discussionsPath(space.id!)}
      testId="bento-discussions"
    >
      {all.length === 0 ? (
        <EmptyHint text="No discussions yet" />
      ) : (
        <div className="flex flex-col divide-y divide-stroke-base">
          {items.map((d: Discussion) => (
            <div key={d.id} className="flex items-start gap-2 py-2 first:pt-0">
              {d.author && <Avatar person={d.author} size={26} />}
              <div className="min-w-0">
                <div className="text-sm font-semibold truncate">{d.title}</div>
                {d.author && <div className="text-xs text-content-dimmed truncate">{d.author.fullName}</div>}
              </div>
            </div>
          ))}
        </div>
      )}
    </Tile>
  );
}

//
// Documents / Resources
//
function DocumentsTile({ space, tools }: { space: Space; tools: SpaceTools }) {
  const paths = usePaths();
  const hubs = tools.resourceHubs ?? [];
  const nodes = hubs.flatMap((h) => h.nodes ?? []);
  const sorted = sortNodesWithFoldersFirst(nodes, "name", "asc");
  const items = sorted.slice(0, 4);
  const viewAllTo = hubs[0] ? paths.resourceHubPath(hubs[0].id!) : paths.spacePath(space.id!);

  return (
    <Tile
      iconWrap="bg-amber-50 dark:bg-amber-900/30"
      icon={<IconFileText size={14} className="text-amber-600 dark:text-amber-400" />}
      title="Documents"
      count={nodes.length}
      viewAllTo={viewAllTo}
      testId="bento-documents"
    >
      {nodes.length === 0 ? (
        <EmptyHint text="No documents yet" />
      ) : (
        <div className="flex flex-col gap-1.5">
          {items.map((node) => (
            <div key={node.id} className="flex items-center gap-2">
              <NodeIcon node={node} size={24} />
              <span className="text-sm truncate">{node.name}</span>
            </div>
          ))}
        </div>
      )}
    </Tile>
  );
}

//
// Tasks
//
function TasksTile({ space, tasks }: { space: Space; tasks: Tasks.Task[] }) {
  const paths = usePaths();
  const items = tasks.slice(0, 6);
  const statusOptions = Tasks.parseTaskStatusesForTurboUi(space.taskStatuses);

  return (
    <Tile
      iconWrap="bg-violet-50 dark:bg-violet-900/30"
      icon={<IconListCheck size={14} className="text-violet-600 dark:text-violet-400" />}
      title="Tasks"
      count={tasks.length}
      viewAllTo={paths.spaceKanbanPath(space.id!)}
      testId="bento-tasks"
    >
      {tasks.length === 0 ? (
        <EmptyHint text="No tasks yet" />
      ) : (
        <div className="flex flex-col gap-1">
          {items.map((task) => (
            <TaskRow key={task.id} task={task} statusOptions={statusOptions} paths={paths} />
          ))}
        </div>
      )}
    </Tile>
  );
}

//
// CRM / Saved Contacts — shared company-wide contact book. Label adapts:
// "CRM" for Sales-type spaces, "Saved Contacts" elsewhere.
//
const CRM_STAGES = ["Lead", "Contacted", "Qualified", "Customer", "Lost"];

const CRM_STATUS_DOT: Record<string, string> = {
  lead: "bg-gray-400",
  contacted: "bg-blue-500",
  qualified: "bg-amber-500",
  customer: "bg-emerald-500",
  lost: "bg-red-500",
};

function CrmTile({ space }: { space: Space }) {
  const paths = usePaths();
  const label = crmToolLabel(space.name);
  const isCrm = label === "CRM";

  const { data, refetch } = useGetCrmContacts({ spaceId: space.id! });
  // Company-wide book: refetch whenever a contact changes anywhere in the company,
  // so this tile ticks in every space at once — not just the one it was added from.
  useReloadContactsSignal(refetch);

  const contacts = data?.contacts ?? [];
  const recent = contacts.slice(0, 4);

  return (
    <Tile
      iconWrap="bg-sky-50 dark:bg-sky-900/30"
      icon={<IconUsers size={13} className="text-sky-600 dark:text-sky-400" />}
      title={label}
      count={contacts.length}
      viewAllTo={paths.spaceCrmPath(space.id!)}
      testId="bento-crm"
    >
      {contacts.length === 0 ? (
        <>
          <div className="text-sm text-content-dimmed">
            {isCrm
              ? "Leads, prospects, and customers. Shared across the company."
              : "Save and look up contacts. Shared across the company."}
          </div>
          {isCrm && (
            <div className="mt-3 flex flex-wrap gap-1">
              {CRM_STAGES.map((s) => (
                <span
                  key={s}
                  className="inline-flex items-center rounded-full border border-surface-outline px-2 py-0.5 text-[10px] font-medium text-content-dimmed"
                >
                  {s}
                </span>
              ))}
            </div>
          )}
        </>
      ) : (
        <div className="flex flex-col divide-y divide-stroke-base">
          {recent.map((c) => (
            <div key={c.id} className="flex items-center justify-between gap-2 py-2 first:pt-0">
              <div className="min-w-0">
                <div className="text-sm font-medium truncate">{c.name}</div>
                {c.company && <div className="text-xs text-content-dimmed truncate">{c.company}</div>}
              </div>
              <span
                className={classNames("w-2 h-2 rounded-full shrink-0", CRM_STATUS_DOT[c.status ?? "lead"] ?? "bg-gray-400")}
                title={c.status ?? "lead"}
              />
            </div>
          ))}
        </div>
      )}
    </Tile>
  );
}

interface TaskRowProps {
  task: Tasks.Task;
  statusOptions: ReturnType<typeof Tasks.parseTaskStatusesForTurboUi>;
  paths: ReturnType<typeof usePaths>;
}

function TaskRow({ task, statusOptions, paths }: TaskRowProps) {
  const status = Tasks.parseTaskStatusForTurboUi(task.status);
  const assignees = (task.assignees || []).flatMap((assignee) => {
    const parsed = People.parsePersonForTurboUi(paths, assignee);
    return parsed ? [parsed] : [];
  });

  return (
    <div className="flex items-center justify-between gap-2 py-1">
      <div className="flex items-center gap-2 min-w-0">
        <div className="flex items-center h-6 shrink-0">
          {status && statusOptions.length > 0 && (
            <StatusSelector statusOptions={statusOptions} status={status} onChange={() => {}} readonly={true} />
          )}
        </div>
        <span className="text-sm truncate">{task.name}</span>
      </div>

      {assignees.length > 0 && (
        <div className="flex items-center shrink-0">
          <AssigneesField people={assignees} setPeople={() => {}} avatarSize={24} avatarOnly={true} readonly={true} />
        </div>
      )}
    </div>
  );
}
